import React, {useCallback} from 'react';
import {EditorState, RichUtils} from 'draft-js';
import styles from '../style.module.scss';
import {Button, Tooltip, Select} from 'antd';
import {Button as V2Button} from '@/components/common-components/v2/Button';
import {Header} from './Header';
import {UnorderedList} from './UnorderedList';
import {CaretDownOutlined, RedoOutlined, UndoOutlined} from '@ant-design/icons';
import {observer} from 'mobx-react-lite';
import {useStore} from '@/store/root-store';
import {Bold} from './Bold';
import {Italic} from './Italic';
import {Underline} from './Underline';
import {OrderedList} from './OrderedList';
import {Code} from './Code';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChevronRight} from '@fortawesome/pro-light-svg-icons';
import {Anchor} from './Anchor';
import classNames from 'classnames';
import styled from 'styled-components';
import {AnchorRemove} from './AnchorRemove';
import {debounce, isNil} from 'lodash';
import {AddImage} from './AddImage/AddImage';
import {faWandMagicSparkles} from '@fortawesome/pro-duotone-svg-icons';
import {toJS} from 'mobx';
import {UploadImage} from './UploadImage';
import {InsertIframe} from './insertIframe';
// import TableGrid from './TableGrid';


export const Toolkit: React.FC = observer(() => {
  const {contentOptimizer:
  {aiOutline: {
    setIsOpenAiWriterDrawer,
    setAiTemplate,
    aiTemplatesController,
    setAiWriter,
  }, currentPage: {
    showAiBanner,
    isTaActive,
    setIsSideBarHidden,
    setIsUnspashDrawerOpen,
    isSideBarHidden,
    aiTooltip,
    analytics,
    setAiTooltip,
    editContent,
    focusTermsClassNames,
    isExportingPage,
    setCurrentEditorState,
    currentEditorState,
    setZoomLevel,
    zoomLevel,
    newPage,
    currentStep,
    isPublic,
    updatingFolder,
  }}} = useStore('');
  const {settings: {customer: {profile: {isLinkgraph}}}} = useStore('');

  const debouncedChange = debounce((state: EditorState, focusTerms, focusTermsClassNames, isNewDecorations = false) => {
    // we are then calling the edit content method which will fire the API call and send new state to the BE
    editContent(state, isNewDecorations);

    if (aiTooltip.show) {
      setAiTooltip({...aiTooltip, show: false, isLinkEdit: false});
    }
  }, 2000);

  const debouncedUpdate = useCallback(
    (state: EditorState, focusTerms, focusTermsClassNames, isNewDecorations) => debouncedChange(state, focusTerms, focusTermsClassNames, isNewDecorations),
    // eslint-disable-next-line
    []
  );

  const onEditorChange = (editorState: EditorState, isNewDecorations: boolean = false) => {
    setCurrentEditorState(editorState);

    // We are comparing current editor state with udpated one to make sure the flow only triggers once there is an actual content change (adding removing of words, formatting, linkjng etc.)
    debouncedUpdate(editorState, analytics?.focusTerms, focusTermsClassNames, isNewDecorations);
  };

  const blockTypeEnhancement = (block: string) => {
    onEditorChange(RichUtils.toggleBlockType(currentEditorState, block), true);
  };

  const inlineStyleEnhancement = (style: string) => {
    onEditorChange(RichUtils.toggleInlineStyle(currentEditorState, style), true);
  };

  const entityTypeEnhancement = (selection: any, entityKey: string) => {
    onEditorChange(RichUtils.toggleLink(currentEditorState, selection, entityKey), true);
  };

  const updateText = (newState: EditorState) => {
    onEditorChange(newState, true);
  };

  const undo = () => {
    onEditorChange(EditorState.undo(currentEditorState), true);
  };

  const redo = () => {
    onEditorChange(EditorState.redo(currentEditorState), true);
  };

  return (
    <div className={styles.toolbar} style={(newPage && currentStep === 1) ? {width: 'calc(100% - 300px)', display: isExportingPage ? 'none' : 'flex'} : {display: isExportingPage ? 'none' : 'flex'}}>
      <div className={styles.leftSection}>
        <Header editorState={!isNil(currentEditorState) ? currentEditorState : EditorState.createEmpty()} blockTypeEnhancement={blockTypeEnhancement} />
        <Bold editorState={currentEditorState} inlineStyleEnhancement={inlineStyleEnhancement} />
        <Italic editorState={currentEditorState} inlineStyleEnhancement={inlineStyleEnhancement} />
        <Underline editorState={currentEditorState} inlineStyleEnhancement={inlineStyleEnhancement} />
        <Anchor updateText={updateText} />
        <AnchorRemove editorState={currentEditorState} entityTypeEnhancement={entityTypeEnhancement} />
        {/* <Highlight editorState={editorState} inlineStyleEnhancement={inlineStyleEnhancement} /> */}
        <OrderedList editorState={currentEditorState} blockTypeEnhancement={blockTypeEnhancement} />
        <UnorderedList editorState={currentEditorState} blockTypeEnhancement={blockTypeEnhancement} />
        {/* <Monospace editorState={editorState} inlineStyleEnhancement={inlineStyleEnhancement} /> */}
        <Code editorState={currentEditorState} blockTypeEnhancement={blockTypeEnhancement} />
        <div className={styles.divider}></div>
        <Tooltip title='Undo'>
          <Button type='text' className={classNames(styles.button, styles.undoBtn)} onClick={undo}><UndoOutlined style={{color: '#4E5156'}} /></Button>
        </Tooltip>
        <Tooltip title='Redo'>
          <Button type='text' className={classNames(styles.button, styles.redoBtn)} onClick={redo}><RedoOutlined style={{color: '#4E5156'}} /></Button>
        </Tooltip>
        <Tooltip title='Zooming feature does not change the text size'>
          <SelectStyled value={zoomLevel} onChange={value => setZoomLevel(value)} suffixIcon={<CaretDownOutlined style={{color: '#121212', pointerEvents: 'none'}} />}>
            <Select.Option value={0.5}>50%</Select.Option>
            <Select.Option value={0.75}>75%</Select.Option>
            <Select.Option value={0.9}>90%</Select.Option>
            <Select.Option value={1}>100%</Select.Option>
            <Select.Option value={1.2}>120%</Select.Option>
            <Select.Option value={1.5}>150%</Select.Option>
            <Select.Option value={2}>200%</Select.Option>
          </SelectStyled>
        </Tooltip>
        <UploadImage updateText={updateText} editorState={currentEditorState}/>
        {isLinkgraph && <InsertIframe updateText={updateText} editorState={currentEditorState}/>}
        {/* <TableGrid editorState={currentEditorState} blockTypeEnhancement={blockTypeEnhancement} /> */}
      </div>
      <div className={styles.rightSection}>
        <AddImage openDrawer={setIsUnspashDrawerOpen} />
        {(!isPublic && !showAiBanner && !isTaActive) &&
          <Tooltip title='Content Ideas'>
            <V2Button
              color='purple'
              disabled={isPublic || updatingFolder}
              loading={updatingFolder}
              style={{padding: '5px 15px', fontWeight: 600, cursor: updatingFolder ? 'not-allowed' : 'pointer'}}
              onClick={() => {
                setIsOpenAiWriterDrawer(true);
                setAiWriter({
                  showForm: true,
                  templateUsed: 'new-ai-writer',
                  templateIcon: 'hand-write-icon-color.svg',
                  templateName: 'Complete SEO Article',
                  templateDesc: 'Generate a full article from just a keyword (topic) or customize the article based on your ranking goals.',
                  isStandaloneTool: false,
                });
                setAiTemplate({
                  ...toJS(aiTemplatesController ?? []),
                  isUseMask: true,
                });
              }}>
              <FontAwesomeIcon icon={faWandMagicSparkles} style={{marginRight: '5px', height: '14px', boxShadow: '0px 3px 8px 0px rgba(0, 0, 0, 0.25)'}} className={styles.robotIcon} />
              <span className={styles.contentIdeasBtnLabel}>
                Write with AI
              </span>
            </V2Button>
          </Tooltip>}

        <Tooltip placement={isSideBarHidden ? 'bottomLeft' : 'bottomRight'} arrowPointAtCenter={true} title={isSideBarHidden ? 'Show Right Sidebar' : 'Hide Right Sidebar'}>
          <div className={styles.expandBtn} onClick={() => setIsSideBarHidden(!isSideBarHidden)}>
            <FontAwesomeIcon icon={faChevronRight} style={{fontSize: 14, transition: 'all .4s ease-in', transform: isSideBarHidden ? 'rotate(-180deg)' : null}} />
          </div>
        </Tooltip>

      </div>
    </div>

  );
});

const SelectStyled = styled(Select)`
  font-size: 12px;
  .ant-select-selector {
    border: 0 !important;
  }
`;
